import React from "react";
import clsx from "clsx";
import { alpha, Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { colorSystem } from "../../theme";

import frame from "../../assets/iphone-frame.jpeg";
import wallpaper from "../../assets/iphone-wallpaper.jpeg";
import phoneIcon from "../../assets/phone-appicon.png";
import safariIcon from "../../assets/safari-appicon.png";
import weatherIcon from "../../assets/weather-appicon.png";
import messagesIcon from "../../assets/messages-appicon.png";
import calculatorIcon from "../../assets/calculator-appicon.png";
import cameraIcon from "../../assets/camera-appicon.png";
import placeholderIcon from "../../assets/placeholder-appicon.png";
import dayjs from "dayjs";

const PHONE_DISPLAY_RATIO = 1.4;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    aspectRatio: `${PHONE_DISPLAY_RATIO} / 1.2`,
    maxWidth: theme.spacing(50),
    borderColor: theme.palette.border.secondary,
    borderWidth: theme.spacing(0.5),
    borderRadius: theme.spacing(2),
    padding: theme.spacing(0, 1),
  },

  title: {
    marginTop: theme.spacing(2),
    color: theme.palette.border.primary,
  },

  phone: {
    width: "100%",
    aspectRatio: `${PHONE_DISPLAY_RATIO} / 1`,
    background: `url(${frame}) no-repeat`,
    backgroundPositionY: "bottom",
    backgroundSize: "cover",
    pointerEvents: "none",
    userSelect: "none",
  },

  screen: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "end",
    height: "96%",
    margin: "0 4% 0 4%",
    borderColor: colorSystem.black,
    background: `url(${wallpaper}) no-repeat`,
    backgroundPositionY: "bottom",
    backgroundSize: "cover",
    borderRadius: `15% / ${PHONE_DISPLAY_RATIO * 15}%`,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderWidth: theme.spacing(0, 1, 1, 1),
  },

  appsRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    padding: "3% 0",
    height: "37%",
    width: "93%",
    maxWidth: "93%",
    marginBottom: "auto",
  },

  dock: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    padding: "3% 0",
    marginBottom: "5%",
    backgroundColor: alpha(colorSystem.white, 0.3),
    backdropFilter: "blur(10px)",
    borderRadius: "8% / 32%",
    width: "93%",
    height: "32%",
  },

  appIcon: {
    height: "100%",
    aspectRatio: "1 / 1",
    borderRadius: "30%",
    backgroundColor: colorSystem.white,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  appIconImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },

  app: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "20%",
  },

  appName: {
    fontSize: 9,
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    color: colorSystem.white,
    textAlign: "center",
    marginTop: theme.spacing(0.2),
    maxWidth: theme.spacing(7),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    height: theme.spacing(1.8),
  },
}));

const CalendarWidget = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: colorSystem.white,
        borderRadius: "8% / 32%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          color: colorSystem.red,
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 0.1,
          marginTop: 2,
        }}
      >
        {dayjs().format("ddd")}
      </Box>
      <Box
        sx={{
          color: colorSystem.black,
          fontSize: 28,
          fontWeight: 300,
          lineHeight: 1.4,
        }}
      >
        {dayjs().format("DD")}
      </Box>
    </Box>
  );
};

interface IphoneIconPreviewProps {
  className?: string;
  iconUrl: string;
  appName: string;
  previewTitle?: string;
}

interface App {
  src?: string;
  widget?: React.ComponentType;
  alt: string;
}

export function IphoneIconPreview(props: IphoneIconPreviewProps) {
  const { className, iconUrl, appName, previewTitle } = props;

  const s = useStyles();

  const appIcons: App[] = [
    { widget: CalendarWidget, alt: "Calendar" },
    { src: calculatorIcon, alt: "Calculator" },
    { src: weatherIcon, alt: "Weather" },
    { src: iconUrl, alt: appName },
  ];

  const dockIcons: App[] = [
    { src: phoneIcon, alt: "Phone" },
    { src: messagesIcon, alt: "Messages" },
    { src: safariIcon, alt: "Safari" },
    { src: cameraIcon, alt: "Camera" },
  ];

  return (
    <Box className={clsx(s.root, className)}>
      <Box className={s.phone}>
        <Box className={s.screen}>
          <Box className={s.appsRow}>
            {appIcons.map((icon, index) => (
              <Box className={s.app} key={index}>
                <Box className={s.appIcon}>
                  {icon.widget ? (
                    <icon.widget />
                  ) : (
                    <img
                      className={s.appIconImage}
                      src={icon.src || placeholderIcon}
                      alt={icon.alt}
                    />
                  )}
                </Box>

                <Box className={s.appName}>{icon.alt}</Box>
              </Box>
            ))}
          </Box>

          <Box className={s.dock}>
            {dockIcons.map((icon, index) => (
              <Box key={index} className={s.appIcon}>
                {icon.widget ? <icon.widget key={index} /> : <img {...icon} />}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Typography className={s.title} variant="h5" textAlign="center">
        {" "}
        {previewTitle || "App Icon Preview"}{" "}
      </Typography>
    </Box>
  );
}
